import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { selectedLevelIndices } from '../lib/iso-levels';

const formatLevel = (v) => (v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toPrecision(3));

const btn = 'flex-1 px-1 py-1 rounded text-[10px] cursor-pointer border';

/** Iso-surface + cutting-plane sections of the docked Volume panel (SubsurfaceViewer3D). */
export default function VolumeIsoControls({ models, iso, setIso, isoInfo, onExport, cut, setCut, cutRange, scale }) {
  const { C } = useTheme();
  const levels = scale?.levels || [];
  const picked = selectedLevelIndices(levels.length, iso.every, iso.picked);
  const toggleLevel = (i) => setIso((s) => {
    const cur = selectedLevelIndices(levels.length, s.every, s.picked);
    return { ...s, picked: cur.includes(i) ? cur.filter((k) => k !== i) : [...cur, i] };
  });
  const patchIso = (p) => setIso((s) => ({ ...s, ...p }));
  const patchCut = (p) => setCut((s) => ({ ...s, ...p }));

  return (
    <>
      {/* Iso-surface */}
      <div data-testid="iso-controls">
        <label className="flex items-center gap-2 text-[10px] mb-1 cursor-pointer" style={{ color: C.text1 }}>
          <input type="checkbox" checked={iso.enabled} disabled={models.length === 0}
            onChange={(e) => patchIso({ enabled: e.target.checked })} className="accent-cyan-400" />
          Iso-surface {models.length === 0 && <span style={{ color: C.text3 }}>(needs a 3D model)</span>}
        </label>
        {iso.enabled && models.length > 0 && (
          <>
            {models.length > 1 && (
              <select value={iso.modelId || models[0].id} onChange={(e) => patchIso({ modelId: e.target.value })}
                className="w-full text-[10px] mb-1 rounded px-1 py-1" style={{ background: C.bg3, color: C.text1, border: `1px solid ${C.border}` }}>
                {models.map((m) => <option key={m.id} value={m.id}>{m.model?.name || m.id}</option>)}
              </select>
            )}
            <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Contour levels ({picked.length} of {levels.length} drawn)</div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-[10px] flex items-center gap-1" style={{ color: C.text2 }}>
                Every
                <input type="number" aria-label="Every Nth level" min={1} max={levels.length || 1} step={1} value={iso.every}
                  onChange={(e) => patchIso({ every: Math.max(1, parseInt(e.target.value, 10) || 1), picked: null })}
                  className="w-10 text-[10px] rounded px-1" style={{ background: C.bg3, color: C.text1, border: `1px solid ${C.border}` }} />
                th
              </label>
              <button type="button" data-testid="iso-levels-reset" onClick={() => patchIso({ every: 3, picked: null })}
                className={btn} style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Reset</button>
            </div>
            <div data-testid="iso-levels" className="overflow-auto mb-1" style={{ maxHeight: 120, border: `1px solid ${C.border}`, borderRadius: 4, padding: 3 }}>
              {levels.map((lv, i) => (
                <label key={i} className="flex items-center gap-1 text-[10px] cursor-pointer" style={{ color: C.text1 }}>
                  <input type="checkbox" checked={picked.includes(i)} onChange={() => toggleLevel(i)} className="accent-cyan-400" />
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: `rgb(${scale.table[i].map(Math.round).join(',')})`, display: 'inline-block' }} />
                  {formatLevel(lv)} Ω·m
                </label>
              ))}
            </div>
            <div className="flex gap-1 my-1">
              {[['level', 'By value'], ['depth', 'By depth']].map(([id, label]) => (
                <button key={id} onClick={() => patchIso({ colorMode: id })}
                  className={`${btn} ${iso.colorMode === id ? 'bg-accent-soft text-accent border-accent-soft' : 'border-transparent'}`}
                  style={iso.colorMode === id ? undefined : { background: C.bg3, color: C.text2 }}>{label}</button>
              ))}
            </div>
            <div className="text-[10px]" style={{ color: C.text2 }}>Opacity: {(iso.opacity * 100).toFixed(0)}%</div>
            <input type="range" min={0.1} max={1} step={0.05} value={iso.opacity}
              onChange={(e) => patchIso({ opacity: parseFloat(e.target.value) })} className="w-full accent-cyan-400" />
            <div className="text-[10px] my-1" style={{ color: isoInfo.error ? '#fbbf24' : C.text3 }}>
              {isoInfo.busy ? 'Extracting…' : isoInfo.error ? isoInfo.error : isoInfo.mesh ? `${isoInfo.meshes.length} surface(s), ${isoInfo.mesh.triangleCount.toLocaleString()} triangles` : ''}
            </div>
            {isoInfo.capped && !isoInfo.busy && (
              <div data-testid="iso-capped-note" className="text-[10px] mb-1 text-amber-400">
                Too many triangles: the highest levels are not drawn. Pick fewer levels.
              </div>
            )}
            <div className="flex gap-1">
              <button disabled={!isoInfo.mesh} onClick={() => onExport('obj')} className={`${btn} disabled:opacity-40`}
                style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Export OBJ</button>
              <button disabled={!isoInfo.mesh} onClick={() => onExport('stl')} className={`${btn} disabled:opacity-40`}
                style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>Export STL</button>
            </div>
          </>
        )}
      </div>

      {/* Cutting plane */}
      <div data-testid="cut-controls">
        <label className="flex items-center gap-2 text-[10px] mb-1 cursor-pointer" style={{ color: C.text1 }}>
          <input type="checkbox" checked={cut.enabled} onChange={(e) => patchCut({ enabled: e.target.checked })} className="accent-amber-400" />
          Cutting plane
        </label>
        {cut.enabled && (
          <>
            <div className="text-[10px]" style={{ color: C.text2 }}>Azimuth: {cut.azimuth.toFixed(0)}°</div>
            <input type="range" min={0} max={360} step={1} value={cut.azimuth}
              onChange={(e) => patchCut({ azimuth: parseFloat(e.target.value) })} className="w-full accent-amber-400" />
            <div className="text-[10px]" style={{ color: C.text2 }}>Dip: {cut.dip.toFixed(0)}° (0 horizontal, 90 vertical)</div>
            <input type="range" min={0} max={90} step={1} value={cut.dip}
              onChange={(e) => patchCut({ dip: parseFloat(e.target.value) })} className="w-full accent-amber-400" />
            <div className="text-[10px]" style={{ color: C.text2 }}>Offset: {cut.offset.toFixed(1)} m</div>
            <input type="range" min={-cutRange} max={cutRange} step={Math.max(0.1, cutRange / 200)} value={cut.offset}
              onChange={(e) => patchCut({ offset: parseFloat(e.target.value) })} className="w-full accent-amber-400" />
            <div className="flex gap-3 mt-1">
              <label className="flex items-center gap-1 text-[10px] cursor-pointer" style={{ color: C.text1 }}>
                <input type="checkbox" checked={cut.flip} onChange={(e) => patchCut({ flip: e.target.checked })} className="accent-amber-400" />
                Flip side
              </label>
              <label className="flex items-center gap-1 text-[10px] cursor-pointer" style={{ color: C.text1 }}>
                <input type="checkbox" checked={cut.showSlice} onChange={(e) => patchCut({ showSlice: e.target.checked })} className="accent-amber-400" />
                Show slice
              </label>
            </div>
          </>
        )}
      </div>
    </>
  );
}
